import { makeAutoObservable, runInAction } from 'mobx'
import { openApi } from '../../../helpers/request'
import i from '../../../translations/i'
import { notification } from 'antd'
import __multiple from '../../../helpers/multiple_actions/__multiple'
import layoutController from '../../../layouts/controllers/layout.controller'
import dayjs from 'dayjs'
import { capturesPayment } from '../order_status/controller'

// Фильтры списка заказов; push_id — заказы, засчитанные рассылке в воронке пушей (переход из «Пушей»).
// Даты — дни (dayjs): создание считается в поясе менеджера, доставка — по часам склада, как её видит клиент
const defaultOrderFilters = () => ({
    id: null,
    status: [],
    payment_status: [],
    client_email: '',
    client_phone: '',
    recipient_phone: '',
    warehouses: [],
    delivery_zones: [],
    user_id: null,
    push_id: null,
    source: null,
    // Способ получения: 'delivery' | 'pickup' | null
    delivery_type: null,
    created_at_from: null,
    created_at_to: null,
    delivery_date_from: null,
    delivery_date_to: null,
    bonuses: false
})

export const ORDER_SOURCES = ['ios', 'android', 'web']
export const ORDER_DELIVERY_TYPES = ['delivery', 'pickup', 'tk']

export type OrdersSort = 'created' | 'delivery_date'

const DAY = 'YYYY-MM-DD'

const urlDay = (value: string | null) => value && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).isValid() ? dayjs(value) : null

// Телефоны в заказах лежат цифрами: «+7 (999) 123-45» ищем как «799912345»
const phoneQuery = (value: string) => {
    const text = (value ?? '').trim()
    return /^[\d\s()+-]+$/.test(text) ? text.replace(/\D/g, '') : text
}

class __order {

    list: any = []

    isLoading = {
        list: false,
        statuses: false
    }

    // Идёт запрос списка; номер запроса — чтобы ответ на устаревший фильтр не перетёр свежий
    listLoading = false
    listRequestId = 0
    filterTimer: any = null

    sort: OrdersSort = 'created'

    item: any = {
        id: 0,
        products: [],
        free_products: [],
        size_bonus_points: 0,
        applicationsLogs: []
    }

    editable: boolean = false

    select = {
        open: false,
        type: 'products'
    }

    usersModal = {
        open: false
    }

    users: any[] = []
    usersFilters = {
        name: null,
        phone: null,
        email: null
    }

    pagination: any = {
        total: 0,
        page: 1,
        limit: 10,
        is_archive: false,
        sum: 0,
        bonuses: null
    }

    statuses: any[] = []
    warehouses: any[] = []

    // Состояние заказа, каким его отдал сервер при последней загрузке
    loaded: { status: any, payment_status: any } = { status: null, payment_status: null }
    paymentWatchId: number = 0

    filterOrders: any = {
        filters: defaultOrderFilters()
    }

    // Панель «Фильтры» по умолчанию свёрнута: ей пользуются не все. Раскрывается сама,
    // если фильтры пришли в адресе (ссылка, переход из карточки клиента)
    filtersOpen: boolean = false

    holding_modal: any = {
        open: false,
        sum: 0
    }

    chargingCard: boolean = false

    availableIntervals: any = null

    constructor() {
        makeAutoObservable(this)
    }

    get params() {
        return this.filterOrders.filters
    }

    get intervalOptions() {
        const group = this.item.place_id ? this.availableIntervals?.pickup : this.availableIntervals?.delivery
        const express = this.item.place_id ? null : this.availableIntervals?.express

        const options = [...(group?.items ?? []), ...(express?.items ?? [])]

        // Интервал заказа мог уже стать недоступным — без него селект показал бы голый id
        if (this.item.delivery_interval_id && !options.some((el: any) => el.id === this.item.delivery_interval_id)) {
            options.unshift({
                id: this.item.delivery_interval_id,
                time_start: this.item.delivery_interval_start,
                time_end: this.item.delivery_interval_end,
                express: this.item.delivery_interval_express,
                in_what_time: this.item.delivery_interval_express_time
            })
        }

        return options
    }

    // Фильтры — в адресе страницы: ссылкой можно поделиться, а из карточки клиента и воронки пушей приходят user_id и push_id
    readFiltersFromUrl() {
        const params = new URLSearchParams(window.location.search)
        const ids = (key: string) => params.getAll(key).map(Number).filter(id => id > 0)
        const filters: any = defaultOrderFilters()

        filters.id = Number(params.get('id')) || null
        filters.user_id = Number(params.get('user_id')) || null
        filters.push_id = Number(params.get('push_id')) || null
        filters.status = ids('status')
        filters.payment_status = ids('payment')
        filters.warehouses = ids('warehouse')
        filters.delivery_zones = ids('zone')
        filters.client_phone = params.get('phone') ?? ''
        filters.client_email = params.get('email') ?? ''
        filters.recipient_phone = params.get('recipient_phone') ?? ''
        filters.source = ORDER_SOURCES.includes(params.get('source') ?? '') ? params.get('source') : null
        filters.delivery_type = ORDER_DELIVERY_TYPES.includes(params.get('delivery_type') ?? '') ? params.get('delivery_type') : null
        filters.created_at_from = urlDay(params.get('created_from'))
        filters.created_at_to = urlDay(params.get('created_to'))
        filters.delivery_date_from = urlDay(params.get('delivery_from'))
        filters.delivery_date_to = urlDay(params.get('delivery_to'))
        filters.bonuses = params.get('bonuses') === '1'

        this.filterOrders.filters = filters
        this.filtersOpen = this.panelFiltersCount > 0
        this.pagination.is_archive = params.get('archive') === '1'
        this.sort = params.get('sort') === 'delivery_date' ? 'delivery_date' : 'created'
        this.pagination.page = 1
    }

    get filtersUrl() {
        const f = this.filterOrders.filters
        const params = new URLSearchParams()

        if (f.id) params.set('id', f.id)
        if (f.user_id) params.set('user_id', f.user_id)
        if (f.push_id) params.set('push_id', f.push_id)
        f.status.forEach((id: number) => params.append('status', `${id}`))
        f.payment_status.forEach((id: number) => params.append('payment', `${id}`))
        f.warehouses.forEach((id: number) => params.append('warehouse', `${id}`))
        f.delivery_zones.forEach((id: number) => params.append('zone', `${id}`))
        if (f.client_phone) params.set('phone', f.client_phone)
        if (f.client_email) params.set('email', f.client_email)
        if (f.recipient_phone) params.set('recipient_phone', f.recipient_phone)
        if (f.source) params.set('source', f.source)
        if (f.delivery_type) params.set('delivery_type', f.delivery_type)
        if (f.created_at_from) params.set('created_from', f.created_at_from.format(DAY))
        if (f.created_at_to) params.set('created_to', f.created_at_to.format(DAY))
        if (f.delivery_date_from) params.set('delivery_from', f.delivery_date_from.format(DAY))
        if (f.delivery_date_to) params.set('delivery_to', f.delivery_date_to.format(DAY))
        if (f.bonuses) params.set('bonuses', '1')
        if (this.pagination.is_archive && !f.push_id) params.set('archive', '1')
        if (this.sort !== 'created') params.set('sort', this.sort)

        const query = params.toString()
        return `${window.location.pathname}${query ? `?${query}` : ''}`
    }

    // Заданы ли фильтры, кроме вкладки «активные / архив» и перехода из пуша
    get filtersActive() {
        const f = this.filterOrders.filters
        return !!(f.id || f.client_phone || f.client_email || this.panelFiltersCount)
    }

    // Сколько фильтров задано в свёрнутой панели «Фильтры» — счётчик на её заголовке
    get panelFiltersCount() {
        const f = this.filterOrders.filters
        return [
            f.delivery_date_from || f.delivery_date_to,
            f.created_at_from || f.created_at_to,
            f.payment_status.length,
            f.warehouses.length,
            f.delivery_zones.length,
            f.source,
            f.delivery_type,
            f.recipient_phone,
            f.user_id,
            f.status.length,
        ].filter(Boolean).length
    }

    toggleFilters() {
        this.filtersOpen = !this.filtersOpen
    }

    // Поменять фильтры и перечитать список с первой страницы; текстовые поля — с паузой на набор
    setFilters(patch: Record<string, any>, debounce: boolean = false) {
        Object.assign(this.filterOrders.filters, patch)
        this.pagination.page = 1

        if (this.filterTimer) clearTimeout(this.filterTimer)
        if (debounce) this.filterTimer = setTimeout(() => this.getList(), 400)
        else this.getList()
    }

    setArchiveTab(isArchive: boolean) {
        this.pagination.is_archive = isArchive
        this.pagination.page = 1
        this.getList()
        __multiple.setActions(this.getActions())
    }

    setSort(sort: OrdersSort) {
        this.sort = sort
        this.pagination.page = 1
        this.getList()
    }

    setOrder() {
        this.editable = true
        this.item = {
            id: 0,
            products: [],
            free_products: [],
            status: 1,
            payment_status: 1,
            payment_type: 1,
            payment_type_id: null,
            delivery: 'default',
            delivery_type: 1,
            user_id: null,
            recipient: 0,
            recipient_name: '',
            recipient_phone: '',
            system_info: 'Cloud web',
            cutlery: 0,
            is_archive: false
        }
    }

    get listQuery() {
        const f = this.filterOrders.filters
        const params = new URLSearchParams({ limit: `${this.pagination.limit}`, page: `${this.pagination.page}` })

        // Заказы после пуша показываются и активные, и архивные — вкладки к ним не применяются
        if (f.push_id) params.set('push_id', f.push_id)
        else if (this.pagination.is_archive) params.set('is_archive', '1')

        if (f.id) params.set('filter_id', f.id)
        if (phoneQuery(f.client_phone)) params.set('filter_client_phone', phoneQuery(f.client_phone))
        if (f.client_email.trim()) params.set('filter_client_email', f.client_email.trim())
        if (phoneQuery(f.recipient_phone)) params.set('filter_recipient_phone', phoneQuery(f.recipient_phone))
        if (f.user_id) params.set('user_id', f.user_id)
        if (f.source) params.set('source', f.source)
        if (f.delivery_type) params.set('filter_delivery_type', f.delivery_type)
        f.status.forEach((id: number) => params.append('filter_status[]', `${id}`))
        f.payment_status.forEach((id: number) => params.append('filter_payment_status[]', `${id}`))
        f.warehouses.forEach((id: number) => params.append('filter_warehouses[]', `${id}`))
        f.delivery_zones.forEach((id: number) => params.append('filter_delivery_zones[]', `${id}`))

        // Создание — границы дня менеджера с его поясом; доставка — календарные дни
        if (f.created_at_from) params.set('created_at_from', f.created_at_from.startOf('day').format())
        if (f.created_at_to) params.set('created_at_to', f.created_at_to.endOf('day').format())
        if (f.delivery_date_from) params.set('delivery_date_from', f.delivery_date_from.format(DAY))
        if (f.delivery_date_to) params.set('delivery_date_to', f.delivery_date_to.format(DAY))

        if (f.bonuses) params.set('bonuses', '1')
        if (this.sort === 'delivery_date') params.set('sort', 'delivery_date')

        // Точка самовывоза — название в карточке заказа
        params.append('with[]', 'place')
        // Карта, привязанная для оплаты после сборки, — пометка в карточке заказа
        params.append('with[]', 'saved_card')

        return `/order?${params.toString()}`
    }

    getList() {
        const requestId = ++this.listRequestId
        this.listLoading = true
        // Выделение — в пределах показанной страницы
        __multiple.clear()

        openApi('get', this.listQuery, {}, undefined, true)
            .then(result => runInAction(() => {
                if (requestId !== this.listRequestId) return

                this.list = result.data
                this.pagination.total = result.total
                this.pagination.sum = result.sum
                this.pagination.bonuses = result.bonuses ?? null
                this.isLoading.list = true
            }))
            .catch(error => console.log(error.response))
            .finally(() => runInAction(() => {
                if (requestId === this.listRequestId) this.listLoading = false
            }))
    }

    setPageSize(page: number, limit: number) {
        if (limit !== this.pagination.limit) {
            this.pagination.limit = limit
            this.pagination.page = 1
        } else {
            this.pagination.page = page
        }
        this.getList()
    }

    setPage(page: number) {
        this.pagination.page = page
        this.getList()
    }

    getOrder(id: number | string) {
        openApi('get', `/order/${id}?with[]=products&with[]=free_products&with[]=promo_code_user&with[]=history&with[]=warehouse&with[]=discounts&with[]=rejection_reason_object&with[]=payment_type_object&with[]=discounts&with[]=saved_card&logs=1`)
            .then(result => {
                console.log(result.data)
                this.item = result.data
                this.loaded = { status: result.data.status, payment_status: result.data.payment_status }

                if (this.item.viewed === 0) {
                    this.view(id)
                }
            })
          .catch(err => {
              console.log(err)
              console.log(err.response)
          })
    }

    setEditable() {
        this.editable = !this.editable

        // Интервалы запрашиваем, только когда менеджер откроет их список или сменит дату
        if (this.editable) {
            this.availableIntervals = null
        }
    }

    getAvailableIntervals(date?: any) {
        if (!this.item.id) return

        const d = date ? dayjs(date) : (this.item.delivery_date ? dayjs(this.item.delivery_date) : dayjs())

        openApi('get', `/order/${this.item.id}/delivery_intervals?day=${d.date()}&month=${d.month() + 1}&year=${d.year()}`)
            .then(result => {
                runInAction(() => {
                    this.availableIntervals = result.data
                })
            })
            .catch(error => {
                runInAction(() => {
                    this.availableIntervals = null
                })
                console.log(error.response)
            })
    }

    changeDeliveryDate(date: any) {
        this.item.delivery_date = date.format()
        this.item.delivery_interval_id = null
        this.item.delivery_interval_start = null
        this.item.delivery_interval_end = null
        this.item.delivery_interval_express = false
        this.item.delivery_interval_express_time = null

        this.getAvailableIntervals(date)
    }

    selectDeliveryInterval(intervalId: any) {
        const interval = this.intervalOptions.find((el: any) => el.id === intervalId)

        if (!interval) return

        this.item.delivery_interval_id = interval.id
        this.item.delivery_interval_start = interval.time_start
        this.item.delivery_interval_end = interval.time_end
        this.item.delivery_interval_express = interval.express
        this.item.delivery_interval_express_time = interval.in_what_time
    }

    save(changeEditable: boolean = true, recalculation: boolean = true) {
        console.log("Save order")
        const data = new FormData()

        if(!this.editable || !recalculation) {
            data.append(`not_recalculation`, `1`)
        }

        let productsCount = 0;
        ['products', 'free_products'].map((type: string) => {
            this.item[type].map((el: any) => {
                data.append(`products[${productsCount}][id]`, `${el.id}`)
                data.append(`products[${productsCount}][product_id]`, `${el.product_id}`)
                data.append(`products[${productsCount}][quantity]`, `${el.count}`)
                data.append(`products[${productsCount}][price]`, `${el.price}`)
                data.append(`products[${productsCount}][free]`, `${+el.free}`)
                productsCount++
            })
        })

        if (productsCount === 0 || this.item.user_id < 1) {
            return notification.error({
                message: i.t('def:errors.error'),
                description: i.t('def:errors.data')
            })
        }

        if (this.item.recipient === 0 || !this.item.recipient) {
            this.item.recipient_name = ''
            this.item.recipient_phone = ''
        }

        if (!this.item.cutlery) {
            this.item.cutlery = 0
        }

        if (!this.item.comment || this.item.comment === 'null') {
            this.item.comment = ''
        }

        data.append('status', this.item.status)
        // Статус оплаты меняется и без экрана — вебхуком оплаты, списанием холда при
        // смене статуса. Отправляем его, только если менеджер сам выбрал другой, иначе
        // устаревшее значение с экрана откатило бы то, что поставил сервер.
        if (!(this.item.id > 0) || this.item.payment_status !== this.loaded.payment_status) {
            data.append('payment_status', this.item.payment_status)
        }
        if(this.item.payment_type) data.append('payment_type', this.item.payment_type)
        if(this.item.payment_type_id) data.append('payment_type_id', this.item.payment_type_id)
        data.append('recipient', this.item.recipient ?? 0)
        data.append('recipient_name', this.item.recipient_name)
        data.append('recipient_phone', this.item.recipient_phone)
        data.append('comment', this.item.comment)
        data.append('cutlery', this.item.cutlery)
        data.append('user_id', this.item.user_id)
        data.append('firstname', this.item.firstname)
        data.append('lastname', this.item.lastname)
        data.append('phone', this.item.phone)
        data.append('email', this.item.email)
        data.append('delivery_type', this.item.delivery_type)
        data.append('delivery', this.item.delivery)
        data.append('delivery_date', this.item.delivery_date)
        if(this.item.delivery_interval_id) data.append('delivery_interval_id', this.item.delivery_interval_id)
        if(this.item.delivery_zone_id) data.append('delivery_zone_id', this.item.delivery_zone_id)
        if(this.item.place_id) data.append('place_id', this.item.place_id)
        if(this.item.delivery_price) data.append('delivery_price', this.item.delivery_price)
        data.append('address', this.item.address)
        data.append('address_full', this.item.address_full)
        data.append('tracking_number', this.item.tracking_number ?? '')

        data.append("size_bonus_points", this.item.__size_bonus_points);

        if(this.item.cashback >= 0) {
            console.log('cashback')
            data.append("cashback", this.item.cashback);
        }

        // Новый статус спишет захолдированные деньги — это случится уже после ответа
        const expectCapture = this.item.id > 0
            && this.item.status !== this.loaded.status
            && this.item.payment_status === 3 && !this.item.holding
            && capturesPayment(this.statuses.find((el: any) => el.id === this.item.status))

        openApi(`post`, `/order/${this.item.id}`, data, { key: 'System-Info', value: this.item.system_info })
            .then(result => {
                if (this.item.id > 0) {
                    if (changeEditable) this.setEditable()
                    this.getOrder(this.item.id)
                    if (expectCapture) this.watchPaymentStatus(this.item.id)
                    notification.success({
                        message: i.t('def:success.success'),
                        description: i.t('def:success.order')
                    })
                } else {
                    if (result.data.id > 0) {
                        window.location.href = `/shop/orders/${result.data.id}`
                    }
                }
                console.log(result)
            })
            .catch(error => {
                console.log(error.response)
            })

    }

    /**
     * Холд списывается в фоновой задаче после сохранения, а статус оплаты ставит вебхук
     * ЮKassa ещё через несколько секунд — ответ на сохранение их не застаёт. Несколько раз
     * тихо перечитываем заказ и обновляем только поля оплаты, чтобы не затереть то,
     * что менеджер уже мог начать править.
     */
    watchPaymentStatus(orderId: number) {
        const watchId = ++this.paymentWatchId
        const startPaymentStatus = this.item.payment_status
        const delays = [2000, 3000, 5000, 8000, 12000, 20000]
        const actual = () => watchId === this.paymentWatchId && this.item.id === orderId

        const check = (attempt: number) => {
            if (attempt >= delays.length) return

            setTimeout(() => {
                if (!actual()) return

                openApi('get', `/order/${orderId}`, {}, undefined, true)
                    .then(result => runInAction(() => {
                        if (!actual()) return

                        // holding меняется раньше статуса оплаты — сразу прячет кнопки холда
                        this.item.holding = result.data.holding

                        if (result.data.payment_status !== startPaymentStatus) {
                            this.item.payment_status = result.data.payment_status
                            this.loaded.payment_status = result.data.payment_status
                            return
                        }

                        check(attempt + 1)
                    }))
                    .catch(() => check(attempt + 1))
            }, delays[attempt])
        }

        check(0)
    }

    deleteProduct(position_id: number, type: string) {
        this.item[type] = this.item[type].filter((el: any) => el.id !== position_id)
    }

    setQuantity(position_id: number, quantity: number, weight_goods: boolean, step: number) {
        let index = this.item.products.findIndex((el: any) => el.id === position_id)
        if (index > -1) {
            if (weight_goods || step > 1) {
                quantity = quantity / step
            }

            this.item.products[index].count = quantity
        } else {
            index = this.item.free_products.findIndex((el: any) => el.id === position_id)

            if (index > -1) {
                if (weight_goods || step > 1) {
                    quantity = quantity / step
                }
                this.item.free_products[index].count = quantity
            }
        }
    }

    // Модалка добавления товара: список, поиск и страницы — у таблицы товаров в ней (Order.tsx)
    setSelect(type: string = '') {
        this.select.open = !this.select.open
        if (this.select.open) this.select.type = type
    }

    setUsers() {
        this.usersModal.open = !this.usersModal.open
        if (this.usersModal.open) {
            this.pagination.page = 1
            this.getUsers()
        }
    }

    getUsers() {

        let offset = this.pagination.page > 1 ? this.pagination.page * this.pagination.limit : 0

        let uri = `/users?limit=${this.pagination.limit}&offset=${offset}&with[]=balance`

        if(this.usersFilters.name) uri += `&filter[name]=${this.usersFilters.name}`
        if(this.usersFilters.phone) uri += `&filter[phone]=${this.usersFilters.phone}`
        if(this.usersFilters.email) uri += `&filter[email]=${this.usersFilters.email}`

        openApi('get', uri)
            .then(result => {
                console.log(result)
                result = result.data
                this.pagination.total = result.count
                this.users = result.list
            })
          .catch(error => console.log(error))
    }

    setUserFilter(key: string, value: any) {
        // @ts-ignore
        this.usersFilters[key] = value
        this.setUsersPage(1)
    }

    setUsersPage(page: number) {
        this.pagination.page = page
        this.getUsers()
    }

    addProduct(product: any) {
        let props: any[] = []

        if (product.props && Array.isArray(product.props)) {
            product.props.map((el: any) => {
                props.push({
                    category: el.category,
                    value: {
                        label: el.label,
                        value: el.value
                    }
                })
            })
        }

        this.item[this.select.type].push({
            id: Date.now(),
            product: product,
            count: 1,
            product_id: product.id,
            free: this.select.type === 'free_products' ? 1 : 0,
            price: this.select.type === 'free_products' ? 0 : product.price,
            props: props
        })

        this.select.open = false
    }

    getStatuses() {
        openApi(`get`, `/order_status`)
            .then(result => {
                console.log(result.data)
                runInAction(() => {
                    this.statuses = result.data
                    this.isLoading.statuses = true
                })
            })
    }

    toggleRecipient() {
        this.item.recipient = this.item.recipient === 1 ? 0 : 1
    }

    toggleUser() {
        if (this.item.user_id < 1) {
            this.setUsers()
        } else {
            this.item.user_id = 0
            this.item.firstname = ''
            this.item.lastname = ''
            this.item.phone = ''
            this.item.email = ''
        }
    }

    addUser(user: any) {
        this.item.user_id = user.id
        this.item.firstname = user.firstname ?? ''
        this.item.lastname = user.lastname ?? ''
        this.item.phone = user.phone ?? ''
        this.item.email = user.email ?? ''

        this.item.user_balance = user.balance.balance

        this.setUsers()
    }

    getDeliveryNameByType(type: string) {
        let r = ''
        if (type === 'sdek') r = i.t('order:sdek')
        else if (type === 'boxbery') r = i.t('order:boxbery')
        else if (type === 'pochta') r = i.t('order:rf')
        else if (type === 'novopochta') r = i.t('order:novopochta')
        else if (type === 'ukrpochta') r = i.t('order:ukrpochta')

        return r
    }

    // Карта для оплаты после сборки (saved_card): «•••• 4242 · Visa · до 04/29», у СБП — банк
    savedCardLabel(card: any) {
        if (!card) return ''
        const expiry = card.expiry_month && card.expiry_year
            ? i.t('order:savedCardExpiry', { date: `${`${card.expiry_month}`.padStart(2, '0')}/${`${card.expiry_year}`.slice(-2)}` })
            : ''

        return [card.label, card.last4 ? card.card_type : '', expiry].filter(Boolean).join(' · ')
    }

    // Сбросить фильтры; переход из пуша снимается своей кнопкой, а подсчёт баллов — не фильтр
    filterClear() {
        const { push_id, bonuses } = this.filterOrders.filters
        this.filterOrders.filters = { ...defaultOrderFilters(), push_id, bonuses }
        this.pagination.page = 1
        this.getList()
    }

    // Убрать фильтр «заказы после пуша» и вернуться ко всем заказам
    clearPushFilter() {
        this.filterOrders.filters.push_id = null
        this.pagination.page = 1
        this.getList()
    }

    // Архив и «просмотрено» одной карточки из списка
    setArchivedFromList(orderID: number, archived: boolean) {
        const data = new FormData()
        data.append('elements[]', `${orderID}`)

        openApi('post', archived ? '/shop/orders/archive/to' : '/shop/orders/archive/from', data)
            .then(() => this.getList())
            .catch(error => console.log(error.response))
    }

    markViewedFromList(orderID: number) {
        const data = new FormData()
        data.append('elements[]', `${orderID}`)

        openApi('post', '/shop/orders/viewed', data, undefined, true)
            .then(() => runInAction(() => {
                const order = this.list.find((el: any) => el.id === orderID)
                if (order) order.viewed = 1
                layoutController.getMarkers()
            }))
            .catch(error => console.log(error.response))
    }

    toArchive(orderID = 0) {
        const data = new FormData()

        if (orderID > 0) {
            data.append('elements[]', `${orderID}`)
        } else {
            __multiple.selected.map(el => data.append('elements[]', `${el}`))
        }

        openApi('post', '/shop/orders/archive/to', data)
            .then(result => {
                if (orderID > 0) {
                    this.getOrder(orderID)
                } else {
                    __multiple.clear()
                    this.getList()
                }
            })
    }

    fromArchive(orderID = 0) {
        const data = new FormData()

        if (orderID > 0) {
            data.append('elements[]', `${orderID}`)
        } else {
            __multiple.selected.map(el => data.append('elements[]', `${el}`))
        }

        openApi('post', '/shop/orders/archive/from', data)
            .then(result => {
                if (orderID > 0) {
                    this.getOrder(orderID)
                } else {
                    __multiple.clear()
                    this.getList()
                }
            })
    }

    viewed() {
        const data = new FormData()

        if (__multiple.selected.length > 0)
            __multiple.selected.map(el => data.append('elements[]', `${el}`))

        openApi('post', '/shop/orders/viewed', data)
            .then(() => {
                __multiple.clear()
                this.getList()
                layoutController.getMarkers()
            })
    }

    getActions() {
        if (this.pagination.is_archive) {
            return [
                { label: i.t('def:fromArchive'), value: 'fromArchive', action: () => this.fromArchive() },
                { label: i.t('def:reading'), value: 'viewed', action: () => this.viewed() }
            ]
        } else {
            return [
                { label: i.t('def:toArchive'), value: 'toArchive', action: () => this.toArchive() },
                { label: i.t('def:reading'), value: 'viewed', action: () => this.viewed() }
            ]
        }
    }

    view(id: number | string) {
        const data = new FormData()

        data.append('elements[]', `${id}`)

        openApi('post', '/shop/orders/viewed', data)
            .then(() => {

            })
    }

    un_holding(orderID: any) {
        if(window.confirm(i.t('def:un_holding_confirm')))
            openApi('post', `/order/un_holding/${orderID}`)
              .then(result => {
                  console.log(result)
                  this.getOrder(orderID)
              })
              .catch(error => console.log(error.response))
    }

    // «Списать с карты»: заказ не оплачен (1 — не оплачен, 901 — оплата после сборки), к нему привязана карта
    canChargeSavedCard() {
        return Number(this.item.save_card_id) > 0 && [1, 901].includes(Number(this.item.payment_status))
    }

    // Одна попытка списания сразу; оплаченным заказ отметит уведомление ЮKassa через пару секунд — перечитываем ещё раз
    chargeSavedCard(orderID: number) {
        this.chargingCard = true

        openApi('post', `/order/charge_saved_card/${orderID}`)
            .then(result => {
                const { status, message } = result.data ?? {}

                if (status === 'succeeded' || status === 'waiting_for_capture') {
                    notification.success({ message: i.t(status === 'succeeded' ? 'order:savedCardChargeSucceeded' : 'order:savedCardChargeHold'), description: message })
                } else if (status === 'pending') {
                    notification.info({ message: i.t('order:savedCardChargePending'), description: message })
                } else {
                    notification.error({ message: i.t(status === 'canceled' ? 'order:savedCardChargeDeclined' : 'order:savedCardChargeError'), description: message, duration: 0 })
                }

                this.getOrder(orderID)

                if (status !== 'canceled' && status !== 'error') {
                    setTimeout(() => this.getOrder(orderID), 3000)
                }
            })
            .catch(error => {
                notification.error({
                    message: i.t('order:savedCardChargeError'),
                    description: error.response?.data?.data?.message ?? i.t('def:errors.somethingWentWrong')
                })
            })
            .finally(() => runInAction(() => this.chargingCard = false))
    }

    closeModalHolding() {
        this.holding_modal.sum = 0
        this.holding_modal.open = false
    }

    holding(orderID: any) {
        this.holding_modal.sum = this.item.sum
        this.holding_modal.open = true
    }

    saveHolding(orderID: any) {
        this.holding_modal.open = false
        const data = new FormData()
        data.append('sum', `${this.holding_modal.sum}`)

        console.log(`${this.holding_modal.sum}`)

        openApi('post', `/order/holding/${orderID}`, data)
          .then(result => {
              this.getOrder(orderID)
          })
          .catch(error => {
              console.log(error.response)
          })
    }

    getUserBalance() {
        let size = this.item.user_balance ?? 0

        let size_bonus_points = this.item.size_bonus_points ?? 0

        if(this.item.__size_bonus_points > size_bonus_points) {
            size = size - (this.item.__size_bonus_points - size_bonus_points)
        } else {

        }

        return size
    }

    repeatSending(orderId: number) {
        if(window.confirm(i.t('def:confirms.genericAction'))) {
            openApi('post', `/order/repeat_sending/${orderId}`)
              .then(result => {
                  this.getOrder(orderId)
              })
              .catch(error => {
                  console.log(error.response)
              })
        }

    }
}

export default new __order()